import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  useToast,
  useDisclosure,
  HStack,
  Progress,
  Spinner,
  Tag,
  TagLabel,
  Text,
  Input,
  chakra,
  useColorModeValue,
} from "@chakra-ui/react";
import { NAVBAR_HEIGHT } from "components/navbar/NavbarAdmin";
import { Column } from "hooks/useTable";
import GamelightTable from "components/table";
import { TableContextProvider } from "contexts/TableContext";
import { FaEdit } from "react-icons/fa";
import { getPartners, getAdminPartners, submitAMUpdate } from "./services";
import { addDays, format } from "date-fns";
import UpdateModal from "./components/UpdateModal";
import { Partner } from "./types";
import { Link } from "react-router-dom";
import useAdmin from "hooks/useAdmin";
import { Get } from "api";
import { SearchableSelect } from "components/filters/SearchableSelect";

const RouterChakraLink = chakra(Link);

export const UPDATE_CATEGORIES = [
  { title: "CPI", color: "green" },
  { title: "Cap", color: "blue" },
  { title: "New game", color: "purple" },
  { title: "Geo", color: "yellow" },
  { title: "Nothing", color: "red" },
] as const;

interface CustomColumn extends Omit<Column<Partner>, "columnKey"> {
  columnKey: string;
}

interface AccountManagerOption {
  id: number;
  firstName: string | null;
  lastName: string | null;
  mail: string | null;
}

const ACCOUNT_MANAGER_SEARCH_KEYS = ["firstName", "lastName", "mail"];

const getAccountManagerDisplayText = (
  accountManager: AccountManagerOption,
) => {
  const fullName = [accountManager.firstName, accountManager.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || accountManager.mail || "Unknown";
};

const getAccountManagerSecondaryText = (accountManager: AccountManagerOption) =>
  accountManager.mail;

const AMUpdates = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd"),
  );
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedAccountManagerId, setSelectedAccountManagerId] = useState<
    number | null
  >(null);
  const [accountManagers, setAccountManagers] = useState<AccountManagerOption[]>(
    [],
  );
  const [isLoadingAccountManagers, setIsLoadingAccountManagers] =
    useState<boolean>(false);
  const [isLoadingPartners, setIsLoadingPartners] = useState<boolean>(false);

  const isAdmin = useAdmin();

  const loadingOverlayBg = useColorModeValue("whiteAlpha.700", "blackAlpha.600");
  const loadingBadgeBg = useColorModeValue("white", "navy.700");
  const textColor = useColorModeValue("gray.700", "white");

  const partnersRequestIdRef = useRef<number>(0);

  useEffect(() => {
    document.title = "Gamelight - CRM";
  }, []);

  const isDateEditable = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    return selected >= today;
  }, [selectedDate]);

  const fetchPartners = useCallback(async () => {
    const requestId = ++partnersRequestIdRef.current;
    setIsLoadingPartners(true);
    try {
      const res = isAdmin
        ? await getAdminPartners(selectedDate, selectedAccountManagerId)
        : await getPartners(selectedDate);
      if (requestId !== partnersRequestIdRef.current) return;
      setPartners(res || []);
    } finally {
      if (requestId === partnersRequestIdRef.current) {
        setIsLoadingPartners(false);
      }
    }
  }, [selectedDate, isAdmin, selectedAccountManagerId]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  useEffect(() => {
    if (!isAdmin) {
      setAccountManagers([]);
      setSelectedAccountManagerId(null);
      return;
    }

    setIsLoadingAccountManagers(true);
    Get("/manage/account-managers")
      .then((response) => {
        if (Array.isArray(response)) {
          setAccountManagers(response);
          return;
        }
        setAccountManagers([]);
      })
      .catch(() => {
        setAccountManagers([]);
      })
      .finally(() => {
        setIsLoadingAccountManagers(false);
      });
  }, [isAdmin]);

  const handleOpenUpdateModal = useCallback(
    (partner: Partner) => {
      if (partner.update && !isDateEditable) return;
      setSelectedPartner(partner);
      onOpen();
    },
    [isDateEditable, onOpen],
  );

  const onSubmit = async (data: {
    updateNote: string;
    categories: string[];
  }) => {
    if (!selectedPartner) return;
    setIsSubmitting(true);
    try {
      const success = await submitAMUpdate({
        partnerId: selectedPartner.id,
        updateCategories: data.categories,
        updateNote: data.updateNote,
        date: addDays(new Date(selectedDate), 1).toISOString(),
        toast,
        onSuccess: () => {
          onClose();
          fetchPartners();
        },
      });
      if (!success) setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  const memoizedColumns = useMemo(() => {
    if (isAdmin) {
      const ADMIN_COLUMNS: CustomColumn[] = [
        {
          columnKey: "companyName",
          header: "COMPANY NAME",
          render: (partner: Partner) => (
            <RouterChakraLink
              to={`/partner/${partner.id}`}
              fontWeight="bold"
              cursor="pointer"
              _hover={{ textDecoration: "underline" }}
            >
              {partner.companyName}
            </RouterChakraLink>
          ),
        },
        {
          columnKey: "updateNote",
          header: "UPDATE NOTE",
          render: (partner: Partner) =>
            partner.update ? (
              <Text>{partner.update.updateNote}</Text>
            ) : (
              <Text fontWeight={"bold"}>-</Text>
            ),
        },
        {
          columnKey: "revenue",
          header: "REVENUE",
          render: (partner: Partner) =>
            partner.revenue ? <Text>{partner.revenue}</Text> : <Text>0</Text>,
        },
        {
          columnKey: "accountManager",
          header: "PRIMARY AM",
          render: (partner: Partner) =>
            partner.primaryAccountManager ? (
              <Text>
                {partner.primaryAccountManager.firstName}{" "}
                {partner.primaryAccountManager.lastName}
              </Text>
            ) : (
              <Text fontWeight={"bold"}>-</Text>
            ),
        },
        {
          columnKey: "accountManagerMail",
          header: "PRIMARY AM MAIL",
          render: (partner: Partner) =>
            partner.primaryAccountManager ? (
              <Text>{partner.primaryAccountManager.mail}</Text>
            ) : (
              <Text fontWeight={"bold"}>-</Text>
            ),
        },
      ];
      return ADMIN_COLUMNS;
    }
    const COLUMNS: CustomColumn[] = [
      {
        columnKey: "companyName",
        header: "COMPANY NAME",
        render: (partner: Partner) => (
          <RouterChakraLink
            to={`/partner/${partner.id}`}
            fontWeight="bold"
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
          >
            {partner.companyName}
          </RouterChakraLink>
        ),
      },
      {
        columnKey: "categories",
        header: "CATEGORIES",
        render: (partner: Partner) =>
          partner.update ? (
            <HStack spacing={2}>
              {partner.update.updateCategories.map((category) => {
                const categoryData = UPDATE_CATEGORIES.find(
                  (c) => c.title === category,
                );
                return (
                  <Tag key={category} colorScheme={categoryData?.color}>
                    <TagLabel>{category}</TagLabel>
                  </Tag>
                );
              })}
            </HStack>
          ) : (
            <Text color="red.500" fontWeight="bold">
              No update
            </Text>
          ),
      },
      {
        columnKey: "updateNote",
        header: "UPDATE NOTE",
        render: (partner: Partner) =>
          partner.update ? (
            <Text>{partner.update.updateNote}</Text>
          ) : (
            <Text color="red.500" fontWeight="bold">
              No update
            </Text>
          ),
      },
      {
        columnKey: "actions",
        header: "ACTIONS",
        render: (partner: Partner) => {
          const buttonProps = {
            leftIcon: <FaEdit />,
            onClick: () => handleOpenUpdateModal(partner),
            isDisabled: !isDateEditable,
            cursor: isDateEditable ? "pointer" : "not-allowed",
            opacity: isDateEditable ? "1" : "0.6",
            _hover: { opacity: isDateEditable ? "0.8" : "0.6" },
            size: "md",
          };

          return (
            <Button
              {...buttonProps}
              colorScheme={partner.update ? "green" : "red"}
            >
              {partner.update ? "Edit Update" : "Update Missing"}
            </Button>
          );
        },
      },
    ];
    return COLUMNS;
  }, [isAdmin, isDateEditable, handleOpenUpdateModal]);

  const fetchTableData = useCallback(
    async () => ({ data: partners, total: partners.length }),
    [partners],
  );

  return (
    <Box flex={1} minH={"90vh"} pt={NAVBAR_HEIGHT} px={4}>
      <Flex direction="column" gap={6} py={6}>
        <Flex gap={4} wrap="wrap" align="end">
          <FormControl width="auto">
            <FormLabel>Select Date</FormLabel>
            <Input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              width="200px"
            />
          </FormControl>
          {isAdmin && (
            <FormControl width="320px">
              <FormLabel>Account Manager</FormLabel>
              <SearchableSelect
                value={selectedAccountManagerId}
                onChange={(value) =>
                  setSelectedAccountManagerId(
                    value === null ? null : Number(value),
                  )
                }
                placeholder="All Account Managers"
                isDisabled={isLoadingAccountManagers}
                options={accountManagers}
                valueKey="id"
                searchKeys={ACCOUNT_MANAGER_SEARCH_KEYS}
                getDisplayText={getAccountManagerDisplayText}
                getSecondaryText={getAccountManagerSecondaryText}
                noOptionsText="No account managers found"
              />
            </FormControl>
          )}
        </Flex>
        <Box position="relative">
          <TableContextProvider>
            <GamelightTable
              columns={memoizedColumns as any}
              fetchData={fetchTableData}
              uniqueKey="id"
              title="Partners"
              shouldSearch
              searchPlaceHolder="Search partners..."
            />
          </TableContextProvider>
          {isLoadingPartners && (
            <Flex
              position="absolute"
              inset={0}
              zIndex={2}
              // matches the Card radius so the overlay does not spill over its corners
              borderRadius="20px"
              bg={loadingOverlayBg}
              backdropFilter="blur(2px)"
              direction="column"
              align="center"
            >
              <Progress
                position="absolute"
                top={0}
                left={0}
                right={0}
                size="xs"
                isIndeterminate
                colorScheme="purple"
                borderTopRadius="20px"
              />
              <Flex
                align="center"
                gap={3}
                mt="100px"
                px={5}
                py={3}
                borderRadius="full"
                bg={loadingBadgeBg}
                boxShadow="md"
              >
                <Spinner size="sm" color="purple.500" thickness="2px" />
                <Text fontSize="sm" fontWeight="medium" color={textColor}>
                  Loading partners...
                </Text>
              </Flex>
            </Flex>
          )}
        </Box>
      </Flex>

      <UpdateModal
        isOpen={isOpen}
        onClose={onClose}
        selectedPartner={selectedPartner}
        isSubmitting={isSubmitting}
        onSubmit={onSubmit}
        isDateEditable={isDateEditable}
      />
    </Box>
  );
};

export default AMUpdates;



